import { Box, Spinner, Text } from 'grommet'
import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { UploadArrowIcon } from '@icons'

interface ResultUploaderProps {
  label: string // e.g. "Lipid Profile", "HIV", "Blood Test"
  onFileUpload: (file: File) => Promise<void> | void
  accept?: Record<string, string[]>
  isLoading?: boolean
}

const defaultAccept = {
  'application/pdf': ['.pdf'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png']
}

const getFileIdentifier = (file: File) => `${file.name}-${file.size}-${file.lastModified}`

const ResultUploader: React.FC<ResultUploaderProps> = ({ label, onFileUpload, accept, isLoading = false }) => {
  const [loadingFiles, setLoadingFiles] = useState<Record<string, boolean>>({})

  const uploadFiles = async (acceptedFiles: File[]) => {
    await Promise.all(
      acceptedFiles.map(async file => {
        const fileId = getFileIdentifier(file)
        setLoadingFiles(prev => ({ ...prev, [fileId]: true }))
        try {
          await onFileUpload(file)
        } finally {
          setLoadingFiles(prev => ({ ...prev, [fileId]: false }))
        }
      })
    )
  }

  const { getInputProps, open } = useDropzone({
    accept: accept || defaultAccept,
    maxSize: 200 * 1024 * 1024,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop: acceptedFiles => {
      void uploadFiles(acceptedFiles)
    }
  })

  return (
    <Box className="border border-solid border-[#EAECF0] rounded-lg p-5">
      <input {...getInputProps()} />

      <Box align="center" direction="row" justify="between">
        <Text className="font-medium text-[#101828]" size="14px">
          {label}
        </Text>
        <Box
          align="center"
          className={`border border-solid border-[#D0D5DD] rounded-lg px-4 py-2 ${
            isLoading ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
          }`}
          direction="row"
          gap="8px"
          onClick={isLoading ? undefined : open}
        >
          <UploadArrowIcon />
          <Text className="font-[500] text-[#101828]" size="14px">
            Upload result
          </Text>
        </Box>
      </Box>

      {Object.entries(loadingFiles).some(([, loading]) => loading) && (
        <Box gap="small" margin={{ top: 'small' }}>
          {Object.entries(loadingFiles)
            .filter(([, loading]) => loading)
            .map(([fileId]) => (
              <Box
                className="flex items-center gap-2 rounded border border-[#EAECF0] bg-[#F8F9FB] px-3 py-2"
                key={fileId}
              >
                <Spinner size="small" />
                <Text className="text-[#101828]" size="14px">
                  Uploading {fileId.split('-').slice(0, -2).join('-')}
                </Text>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  )
}

export default ResultUploader
